// ════════════════════════════════════════════════════════════════════════════
// BlocoAcaoModal — r147
// Modal que abre ao clicar em um bloco do calendário.
// Mostra info do bloco + ações:
//  - Marcar como Executada (só pra TAREFA_PLANEJADA)
//  - Cancelar Planejamento  (só pra TAREFA_PLANEJADA)
//  - Abrir Tarefa (sempre, se tem tarefa_id)
//  - Excluir Apontamento (só pra TAREFA / executada)
// ════════════════════════════════════════════════════════════════════════════

import React from 'react';

const COR = {
  azul:    '#003399',
  azulBg:  '#eff6ff',
  borda:   '#e8eaed',
  texto:   '#1a1a2e',
  muted:   '#888',
  branco:  '#fff',
  verde:   '#1D9E75',
  verdeBg: '#f0fff4',
  amarelo: '#d97706',
  amlBg:   '#fffbeb',
  vermelho:'#e53935',
  vermBg:  '#fff0f0',
  cinzaBg: '#fafbfc',
};

function fmtDataHora(d) {
  if (!d) return '—';
  return new Date(d).toLocaleString('pt-BR', {
    weekday: 'short', day: '2-digit', month: '2-digit',
    hour: '2-digit', minute: '2-digit',
  });
}

function calcDuracao(ini, fim) {
  const ms = new Date(fim) - new Date(ini);
  const h = Math.round((ms / 1000 / 60 / 60) * 100) / 100;
  return `${h.toFixed(2)}h`;
}

function BlocoAcaoModal({
  aberto, aloc,
  onMarcarExecutada, onCancelarPlanejamento, onAbrirTarefa,
  onExcluirApontamento,    // r149: novo (pra TAREFA)
  onFechar, processando,
}) {
  if (!aberto || !aloc) return null;

  const isPlanejada = aloc.tipo === 'TAREFA_PLANEJADA';
  const isExecutada = aloc.tipo === 'TAREFA';
  const corHeader = isPlanejada ? COR.azul : (isExecutada ? COR.verde : COR.amarelo);
  const iconeHeader = isPlanejada ? '📅' : (isExecutada ? '⏱' : '📌');
  const titulo = isPlanejada ? 'Bloco Planejado' : (isExecutada ? 'Apontamento de Horas' : 'Compromisso');

  return (
    <div
      onClick={onFechar}
      style={{
        position: 'fixed', inset: 0,
        backgroundColor: 'rgba(0,0,0,0.5)',
        zIndex: 1100,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          backgroundColor: COR.branco,
          borderRadius: '8px',
          width: '480px', maxWidth: '92vw',
          overflow: 'hidden',
          fontFamily: "'Manrope', sans-serif",
          boxShadow: '0 4px 24px rgba(0,0,0,0.25)',
        }}
      >
        {/* Header */}
        <div style={{
          backgroundColor: corHeader, color: COR.branco,
          padding: '12px 16px',
          display: 'flex', alignItems: 'center', gap: '10px',
        }}>
          <span style={{ fontSize: '18px' }}>{iconeHeader}</span>
          <span style={{ fontSize: '15px', fontWeight: '700', flex: 1 }}>{titulo}</span>
          <button onClick={onFechar} disabled={processando} style={btnFechar}>×</button>
        </div>

        {/* Corpo */}
        <div style={{ padding: '16px' }}>

          {/* Tarefa */}
          {aloc.tarefa_id && (
            <div style={{
              padding: '10px 12px', borderRadius: '5px',
              backgroundColor: COR.cinzaBg, border: `1px solid ${COR.borda}`,
              marginBottom: '12px',
            }}>
              <div style={{ fontSize: '11px', color: COR.muted, marginBottom: '4px' }}>Tarefa</div>
              <div style={{ fontSize: '13px', fontWeight: '600', color: COR.texto }}>
                #{aloc.tarefa_id} — {aloc.tarefa_titulo || '(sem título)'}
              </div>
              {aloc.cliente_fantasia && (
                <div style={{ fontSize: '11px', color: COR.muted, marginTop: '3px' }}>
                  {aloc.cliente_fantasia}
                </div>
              )}
            </div>
          )}

          {/* Período */}
          <div style={{ marginBottom: '14px', fontSize: '12px', color: COR.texto, lineHeight: 1.7 }}>
            <div><b style={{ color: COR.muted }}>Início:</b> {fmtDataHora(aloc.data_inicio)}</div>
            <div><b style={{ color: COR.muted }}>Fim:&nbsp;&nbsp;&nbsp;</b> {fmtDataHora(aloc.data_fim)}</div>
            <div><b style={{ color: COR.muted }}>Duração:</b> {calcDuracao(aloc.data_inicio, aloc.data_fim)}</div>
          </div>

          {/* Observação (se houver) */}
          {aloc.observacao && (
            <div style={{
              padding: '8px 10px', borderRadius: '4px',
              backgroundColor: COR.cinzaBg, fontSize: '11px',
              color: COR.muted, marginBottom: '12px',
              fontStyle: 'italic',
            }}>
              "{aloc.observacao}"
            </div>
          )}
        </div>

        {/* Rodapé com ações */}
        <div style={{
          padding: '12px 16px', borderTop: `1px solid ${COR.borda}`,
          backgroundColor: COR.cinzaBg,
          display: 'flex', justifyContent: 'flex-end', gap: '8px', flexWrap: 'wrap',
        }}>
          {/* Abrir tarefa (sempre, se tem tarefa) */}
          {aloc.tarefa_id && onAbrirTarefa && (
            <button onClick={onAbrirTarefa} disabled={processando} style={btnSec}>
              📋 Abrir Tarefa
            </button>
          )}

          {/* Ações de planejada */}
          {isPlanejada && (
            <>
              <button onClick={onCancelarPlanejamento} disabled={processando} style={btnVerm}>
                ✕ Cancelar Planejamento
              </button>
              <button onClick={onMarcarExecutada} disabled={processando} style={btnVerde}>
                {processando ? 'Processando...' : '✓ Marcar como Executada'}
              </button>
            </>
          )}

          {/* r149: Ação de executada (apontamento) — excluir + fechar */}
          {isExecutada && (
            <>
              <button onClick={onExcluirApontamento} disabled={processando} style={btnVerm}>
                {processando ? 'Processando...' : '🗑 Excluir Apontamento'}
              </button>
              <button onClick={onFechar} disabled={processando} style={btnSec}>Fechar</button>
            </>
          )}

          {/* Fechar pra outros tipos */}
          {!isPlanejada && !isExecutada && (
            <button onClick={onFechar} disabled={processando} style={btnSec}>Fechar</button>
          )}
        </div>
      </div>
    </div>
  );
}

const btnFechar = {
  background: 'transparent', border: 'none', color: COR.branco,
  fontSize: '20px', cursor: 'pointer', padding: '0 4px', lineHeight: 1,
};
const btnBase = {
  padding: '8px 14px', borderRadius: '5px',
  fontSize: '12px', fontWeight: '600', cursor: 'pointer',
  fontFamily: "'Manrope', sans-serif",
  whiteSpace: 'nowrap',
};
const btnSec = {
  ...btnBase,
  border: `1px solid ${COR.borda}`,
  backgroundColor: COR.branco, color: COR.texto,
};
const btnVerm = {
  ...btnBase,
  border: `1px solid ${COR.vermelho}`,
  backgroundColor: COR.branco, color: COR.vermelho,
};
const btnVerde = {
  ...btnBase,
  border: `1px solid ${COR.verde}`,
  backgroundColor: COR.verde, color: COR.branco,
};

export default BlocoAcaoModal;
